/**
 * WHERE EVERY TRADE CARD SITS, as pure arithmetic over pixels.
 *
 * <h2>Why the cards left the series and became `graphic` elements</h2>
 *
 * They were labels on a scatter series, which bought data-coordinate tracking for free — the card
 * followed its bar through every zoom with no pixel maths at all. That is given up here, and the
 * reason is DRAGGING.
 *
 * Three routes were considered and two were ruled out by evidence rather than by taste:
 *
 * <ol>
 *   <li><b>An HTML overlay.</b> Ruled out: this app exports screenshots through
 *       {@code chart.getDataURL()} (see utils/operatorExport.js and utils/chartSweepCapture.js),
 *       which renders the ECharts canvas and nothing else. HTML cards would be invisible in every
 *       exported image — failing silently at the exact moment the feature exists for, since the
 *       whole point of dragging them is to compose a screenshot.</li>
 *   <li><b>Dragging the scatter labels.</b> Ruled out: a series label is not hit-testable.
 *       Verified on a live chart — {@code zr.handler.findHover} over a card's centre returns the
 *       background rect, not the label, even with the series un-silenced. Dragging them would mean
 *       hand-rolled hit-testing, poking zrender elements directly, and a separate update path for
 *       the leader line — a lot of bespoke machinery around a library that is telling us no.</li>
 *   <li><b>`graphic` elements with {@code draggable: true}.</b> Chosen. Native dragging, renders
 *       into the canvas so exports keep the cards, and the leader line is ours to move. Verified
 *       with a real mouse drag on a live chart: the element moved exactly the dragged delta.</li>
 * </ol>
 *
 * The price is that positions are PIXELS, so they must be recomputed whenever the mapping from
 * data to pixels changes — zoom, pan, resize. That is what {@link layoutCards} is for, and it is
 * pure so it can be tested without a chart.
 *
 * <h2>The cards are pushed to the GRID EDGES</h2>
 *
 * Not hovering over the wick. Price action gets the middle of the chart and the cards form two
 * bands at the top and bottom, each tethered back to its own marker by a long leader line. At the
 * densities this feature is used at that is the difference between a readable chart and a wall.
 */

import { DECORATOR_TYPE, DECORATOR_CHAR_WIDTH, CARD_SIDE } from './tradeDecorator';

/** How far a card's outer edge sits from the edge of the plot. */
export const CARD_EDGE_PAD = 8;

/** Vertical air between two cards stacked in the same band. */
export const CARD_STACK_GAP = 3;

/**
 * How far INTO the chart the packer may push a card to clear its neighbours.
 *
 * <p>A fraction of the plot height, not a card count: the point of the edge placement is to leave
 * the middle alone, and a band allowed to grow without limit just reproduces the wall it replaced.
 * Cards past this are left overlapping at the limit — which is honest, and is what dragging is
 * for. Hiding them would be the `hideOverlap` mistake in a new costume.
 */
export const CARD_BAND_FRACTION = 0.42;

/**
 * The plain text of each rendered line, with the rich markup taken off.
 *
 * <p>EVERY tag on the line, not just the first. The pattern deliberately mirrors zrender's own
 * {@code STYLE_REG} — {@code /\{([a-zA-Z0-9_]+)\|([^}]*)\}/g} — so what this measures is exactly
 * what that renders. Stripping only a leading tag and a trailing brace was correct while a line
 * was always one tag, and silently over-measures a line built from two: the yearly summary's
 * {@code {k|W/L    }{v|5W | 2L}} would have counted its own markup as text and reserved a wider
 * box than it draws.
 *
 * <p>The `|` inside a value is safe for the same reason it is safe in zrender: the content group
 * is `[^}]*`, so only a closing brace ends a tag.
 */
export const richLines = (rich) =>
  String(rich == null ? '' : rich)
    .split('\n')
    .map((l) => l.replace(/\{([a-zA-Z0-9_]+)\|([^}]*)\}/g, '$2'));

/**
 * Each line broken into its `{style|text}` SEGMENTS.
 *
 * <p>A line can carry more than one — `{k|NET    }{win|+12.34%}` is a label and an accented value
 * — and once they are allowed DIFFERENT SIZES the line's width is the sum of its parts rather
 * than one character count. Untagged text becomes a segment with a null style, which then takes
 * the card's base type.
 */
export const richSegments = (rich) => String(rich == null ? '' : rich)
  .split('\n')
  .map((line) => {
    const out = [];
    const re = /\{([a-zA-Z0-9_]+)\|([^}]*)\}/g;
    let last = 0;
    let m = re.exec(line);
    while (m !== null) {
      if (m.index > last) out.push({ style: null, text: line.slice(last, m.index) });
      out.push({ style: m[1], text: m[2] });
      last = re.lastIndex;
      m = re.exec(line);
    }
    if (last < line.length) out.push({ style: null, text: line.slice(last) });
    return out.length > 0 ? out : [{ style: null, text: '' }];
  });

/**
 * THE ADVANCE OF ONE CHARACTER, per pixel of font size.
 *
 * <p>Measured with `measureText` across 10–16px and weights 400/600/700: the advance is
 * `0.5498 × fontSize` in every one of them, to four decimal places. It is exact rather than
 * approximate because the family ends in `monospace` — every glyph is one em-fraction wide, and
 * weight does not change an advance.
 */
export const CHAR_WIDTH_RATIO = DECORATOR_CHAR_WIDTH / DECORATOR_TYPE.fontSize;

/**
 * A card's rendered size, computed rather than measured.
 *
 * <p>Exact because the face is monospaced. Measuring instead would mean building the element,
 * laying it out, reading it back and then placing it — three extra passes over several hundred
 * cards on every zoom tick.
 *
 * <h3>IT HANDLES MIXED TYPE, and that is why the plaques could get bigger</h3>
 *
 * It used to multiply the longest line by ONE character width, which is only right while every
 * line is the same size. That was written down as the reason the yearly summary had to keep the
 * trade cards' 10px scale: a larger header would have been under-measured and the packer would
 * have let the next card overlap it. Now each SEGMENT is measured at its own style's size, so a
 * 15px header and a 13px label on the same card are both exact.
 *
 * <p>Padding comes from the card too, so a plaque can be roomier than a card without the box
 * arithmetic knowing anything about plaques.
 *
 * <p>The SHADOW is deliberately not in the box. It is soft and has no edge, so reserving space
 * for it would push real content apart to protect a blur.
 *
 * @param rich    the formatter string
 * @param styles  the card's `rich` style map; absent, every segment takes the base type
 * @param padding `[top, right, bottom, left]`; absent, the trade cards' own
 * @param borderWidth drawn on each side, and part of what a neighbour must clear
 */
export const cardBox = (rich, { styles, padding, borderWidth = 1 } = {}) => {
  const [padTop, padRight, padBottom, padLeft] = padding || DECORATOR_TYPE.padding;
  const lines = richSegments(rich);
  let width = 0;
  let height = 0;
  lines.forEach((segments) => {
    let lineWidth = 0;
    let lineHeight = 0;
    segments.forEach((seg) => {
      const style = (styles && seg.style && styles[seg.style]) || {};
      const size = Number.isFinite(style.fontSize) ? style.fontSize : DECORATOR_TYPE.fontSize;
      const lead = Number.isFinite(style.lineHeight) ? style.lineHeight : DECORATOR_TYPE.lineHeight;
      lineWidth += seg.text.length * size * CHAR_WIDTH_RATIO;
      lineHeight = Math.max(lineHeight, lead);
    });
    width = Math.max(width, lineWidth);
    // A line's height is its TALLEST segment -- mixed sizes on one line sit on one baseline.
    height += lineHeight;
  });
  return {
    width: Math.ceil(width) + padLeft + padRight + borderWidth * 2,
    height: Math.ceil(height) + padTop + padBottom + borderWidth * 2,
  };
};

const overlaps = (a, b) =>
  a.x < b.x + b.width && b.x < a.x + a.width
  && a.y < b.y + b.height && b.y < a.y + a.height;

/**
 * PLACES EVERY CARD, and returns one placement each.
 *
 * <p>Order of business, and each step exists for a reason:
 *
 * <ol>
 *   <li><b>Dragged cards are placed first and never moved.</b> A card the reader has positioned by
 *       hand is the one thing the packer must not second-guess — and it becomes an OBSTACLE, so
 *       the automatic ones get out of its way rather than piling onto it.</li>
 *   <li><b>The rest start at their band's edge</b>, horizontally centred on their anchor.</li>
 *   <li><b>Then they are pushed inward until they clear</b> everything already placed on the chart
 *       — including the other band's cards, since a tall card from below can reach a short one
 *       from above on a small chart.</li>
 * </ol>
 *
 * <p>Left to right within a band, so the stacking reads in time order rather than in whatever
 * order the trades happened to be listed.
 *
 * @param cards    [{key, side, rich}] — content and which band
 * @param anchors  {key: [x, y]} in pixels; a card with no anchor is skipped
 * @param grid     {left, right, top, bottom} the plot rectangle, in pixels
 * @param offsets  {key: {dx, dy}} manual drags, as a delta from the card's own default
 */
export const layoutCards = ({ cards, anchors, grid, offsets = {} }) => {
  if (!Array.isArray(cards) || !grid) return [];
  const bandDepth = Math.max(0, (grid.bottom - grid.top)) * CARD_BAND_FRACTION;

  const prepared = cards
    .map((card) => {
      const anchor = anchors && anchors[card.key];
      if (!anchor || !Number.isFinite(anchor[0]) || !Number.isFinite(anchor[1])) return null;
      // OFF-SCREEN ANCHORS ARE DROPPED, and this is not a nicety.
      //
      // `convertToPixel` does not clamp -- for a bar outside the zoom window it returns an
      // EXTRAPOLATED pixel, which can be thousands of pixels past the plot. Those then hit the
      // in-plot clamp below and every one of them lands on the same edge. Measured on a real
      // chart zoomed to one year of a 33-year run: 498 of 516 cards piled into a column against
      // the right edge, which is both unreadable and the opposite of what the edges are for.
      //
      // A card belongs to a MARKER. If the marker is not on screen the card has nothing to point
      // at, so it is not drawn.
      if (anchor[0] < grid.left || anchor[0] > grid.right
        || anchor[1] < grid.top || anchor[1] > grid.bottom) return null;
      // Measured with the CARD'S OWN type, padding and rule -- a plaque is bigger than a
      // trade card in all three, and a packer measuring it as a card would let the next
      // one overlap it.
      const box = cardBox(card.rich, {
        styles: card.richStyles, padding: card.padding, borderWidth: card.borderWidth,
      });
      const below = card.side === CARD_SIDE.BELOW;
      // The DEFAULT: hard against the band's edge, centred on the anchor and kept inside the plot.
      const x = Math.min(Math.max(anchor[0] - box.width / 2, grid.left + CARD_EDGE_PAD),
        grid.right - CARD_EDGE_PAD - box.width);
      const y = below
        ? grid.bottom - CARD_EDGE_PAD - box.height
        : grid.top + CARD_EDGE_PAD;
      return { ...card, ...box, anchor, below, baseX: x, baseY: y };
    })
    .filter(Boolean)
    .sort((a, b) => a.anchor[0] - b.anchor[0]);

  const placed = [];
  const settle = (c, x, y, moved) => {
    // baseX/baseY are the card's DEFAULT, before packing and before any drag. A drag is stored
    // as a delta from this, not from where the card happens to have been pushed to -- the packed
    // position moves as other cards are opened and closed, so an offset measured against it
    // would drift every time the set changed.
    const p = { key: c.key, side: c.side, anchor: c.anchor, width: c.width, height: c.height,
      x, y, baseX: c.baseX, baseY: c.baseY, moved };
    placed.push(p);
    return p;
  };

  // (1) manual placements, as fixed obstacles
  const dragged = new Set();
  prepared.forEach((c) => {
    const off = offsets[c.key];
    if (!off) return;
    dragged.add(c.key);
    settle(c, c.baseX + off.dx, c.baseY + off.dy, true);
  });

  // (2) and (3) the automatic ones, pushed inward until clear
  prepared.forEach((c) => {
    if (dragged.has(c.key)) return;
    const step = c.height + CARD_STACK_GAP;
    let y = c.baseY;
    for (let depth = 0; depth <= bandDepth; depth += step) {
      const candidate = { x: c.baseX, y, width: c.width, height: c.height };
      if (!placed.some((p) => overlaps(candidate, p))) break;
      y += c.below ? -step : step;
    }
    settle(c, c.baseX, y, false);
  });

  // Back into the caller's order, so a card's identity does not depend on where it landed.
  const byKey = new Map(placed.map((p) => [p.key, p]));
  return cards.map((c) => byKey.get(c.key)).filter(Boolean);
};

/**
 * The leader line from a card back to its marker.
 *
 * <p>It meets the card at the centre of the edge FACING the anchor, so the line never crosses the
 * box it belongs to. Two points, not an elbow: a straight line to a distant anchor is already
 * unambiguous, and a dogleg at this density reads as another box.
 */
export const leaderPoints = (placement) => {
  if (!placement || !placement.anchor) return [];
  return leaderFor(placement.anchor, placement.x, placement.y,
    placement.width, placement.height, placement.side);
};

/**
 * The same line, from loose parts.
 *
 * <p>Taken separately so the DRAG handler can recompute it from the element's live position
 * without building a placement object on every mouse move.
 */
export const leaderFor = (anchor, x, y, width, height, side) => {
  const cx = x + width / 2;
  const edgeY = side === CARD_SIDE.BELOW
    ? y                 // card is below the anchor: meet its TOP edge
    : y + height;       // card is above: meet its BOTTOM edge
  return [[anchor[0], anchor[1]], [cx, edgeY]];
};

/* ── the ECharts elements ─────────────────────────────────────────────────────────────────── */

/** Everything a card graphic's id starts with, so the chart can tell its own from a watermark. */
export const CARD_GRAPHIC_PREFIX = 'trade-card-';

/**
 * ONE GRAPHIC GROUP PER CARD: a leader line and a draggable box.
 *
 * <h3>Why the GROUP is not the draggable thing</h3>
 *
 * Dragging the group would carry the leader line with it, so the line would keep pointing at
 * wherever the anchor USED to be relative to the card — which is the one thing it must never do.
 * The TEXT is draggable and the line is its sibling; the drag handler reaches the line through
 * {@code this.parent} and re-points it. That is also why they are grouped at all rather than
 * being two loose elements: the parent link is what makes the lookup free.
 *
 * @param placements from {@link layoutCards}
 * @param cards      the descriptors, for content and colours
 * @param onMoved    (key, {dx, dy}) once a drag finishes — the caller persists it
 */
export const cardGraphics = ({ placements, cards, onMoved }) => {
  const byKey = new Map((cards || []).map((c) => [c.key, c]));
  return (placements || []).map((p) => {
    const card = byKey.get(p.key);
    if (!card) return null;
    const theme = card.theme || {};
    // A YEARLY SUMMARY has no leader. It describes a year, not a trade, so there is no marker for
    // it to point at -- and a line dropped to an invented price would assert a level nothing
    // computed. The child is still EMITTED, as an empty polyline, so `childAt(0)` means the same
    // thing for every card and the drag handler needs no special case.
    const leader = card.leader !== false;
    return {
      type: 'group',
      id: `${CARD_GRAPHIC_PREFIX}${p.key}`,
      z: 120,
      children: [
        {
          type: 'polyline',
          // SILENT: the line is a thread, and a click on it should reach the chart underneath.
          silent: true,
          z: 119,
          shape: { points: leader ? leaderPoints(p) : [] },
          style: { stroke: theme.border, lineWidth: 1, opacity: 0.55, fill: 'none' },
        },
        {
          type: 'text',
          draggable: true,
          cursor: 'move',
          x: p.x,
          y: p.y,
          z: 121,
          style: {
            text: card.rich,
            rich: card.richStyles,
            // x,y is the box's TOP-LEFT with these two, which is what the layout computed.
            align: 'left',
            verticalAlign: 'top',
            fill: theme.text,
            backgroundColor: theme.background,
            borderColor: theme.border,
            // A plaque asks for a heavier rule; a trade card does not set this at all.
            borderWidth: Number.isFinite(card.borderWidth) ? card.borderWidth : 1,
            padding: card.padding || DECORATOR_TYPE.padding,
            fontFamily: DECORATOR_TYPE.fontFamily,
            fontSize: DECORATOR_TYPE.fontSize,
            lineHeight: DECORATOR_TYPE.lineHeight,
            // THE DROP SHADOW, which is what lifts a plaque off the price action. Only set when
            // the card asks for one -- a shadow under all 516 trade cards would be a grey wash.
            ...(Number.isFinite(card.shadowBlur)
              ? { shadowBlur: card.shadowBlur,
                shadowColor: card.shadowColor || 'rgba(0,0,0,0.5)',
                shadowOffsetX: 0,
                shadowOffsetY: Number.isFinite(card.shadowOffsetY) ? card.shadowOffsetY : 2 }
              : {}),
          },
          ondrag() {
            // TETHERED WHILE DRAGGING. The line is re-pointed on every move rather than on drop,
            // because a card that detaches mid-drag and snaps back is how a reader loses track of
            // which marker they were moving. A plaque has no line and skips this entirely.
            if (!leader) return;
            const line = this.parent && typeof this.parent.childAt === 'function'
              ? this.parent.childAt(0) : null;
            if (!line || typeof line.setShape !== 'function') return;
            line.setShape({ points: leaderFor(p.anchor, this.x, this.y, p.width, p.height, p.side) });
          },
          ondragend() {
            if (typeof onMoved !== 'function') return;
            // Stored against the card's own DEFAULT, so it survives a zoom: the default moves
            // with the bar and the card moves with it, keeping the reader's arrangement.
            onMoved(p.key, { dx: this.x - p.baseX, dy: this.y - p.baseY });
          },
        },
      ],
    };
  }).filter(Boolean);
};
